/**
 * Level15Scene - 关卡15：相对论综合挑战
 * @description 综合运用狭义相对论知识：光速限制、时间膨胀、长度收缩、红移蓝移
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import { PhysicsWorld } from '@/physics/PhysicsWorld.ts';
import { Stickman } from '@/characters/Stickman.ts';
import { Relativity, SPEED_OF_LIGHT } from '@/physics/Relativity.ts';
import type { LevelConfig, CheckpointConfig, StageConfig } from '@/levels/index.ts';
import { getGameState } from '@/core/GameState.ts';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

type GameState = 'idle' | 'running' | 'success' | 'failed';
type CurrentStage = 1 | 2 | 3 | 4;

// 各阶段主题颜色
const STAGE_COLORS: Record<CurrentStage, number> = {
  1: 0xedc240, // 金色 - 光速限制阶段
  2: 0x28a745, // 绿色 - 时间膨胀阶段
  3: 0x00ccff, // 蓝色 - 长度收缩阶段
  4: 0xff6b6b, // 红色 - 红移蓝移阶段
};

const CHECKPOINT_COLOR = 0xffa500;
const GOAL_COLOR = 0x4ade80;

interface RelativityLevelConfig extends LevelConfig {
  velocityGates?: Array<{
    id: string;
    position: { x: number; y: number };
    targetVelocity: number;
    size: { width: number; height: number };
  }>;
  timeDilationZones?: Array<{
    id: string;
    position: { x: number; y: number };
    velocity: number;
    size: { width: number; height: number };
  }>;
  lengthContractionZones?: Array<{
    id: string;
    position: { x: number; y: number };
    velocity: number;
    direction: { x: number; y: number };
    size: { width: number; height: number };
  }>;
  dopplerZones?: Array<{
    id: string;
    position: { x: number; y: number };
    velocity: number;
    isApproaching: boolean;
    size: { width: number; height: number };
  }>;
}

export class Level15Scene extends Phaser.Scene {
  private physicsWorld!: PhysicsWorld;
  private stickman!: Stickman;
  private relativity!: Relativity;
  private levelConfig: RelativityLevelConfig | null = null;
  private gameState: GameState = 'idle';
  private currentStage: CurrentStage = 1;
  private checkpoints: CheckpointConfig[] = [];
  private stages: StageConfig[] = [];
  private completedStages: Set<number> = new Set();
  private checkpointGraphics: Map<string, Phaser.GameObjects.Graphics> = new Map();
  private goalZone!: Phaser.Geom.Rectangle;
  private isVictory: boolean = false;

  // UI 文本元素
  private stageText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;
  private formulaText!: Phaser.GameObjects.Text;
  private velocityText!: Phaser.GameObjects.Text;
  private gammaText!: Phaser.GameObjects.Text;
  private timeDisplayText!: Phaser.GameObjects.Text;

  // 时间追踪
  private elapsedTime: number = 0;
  private dilatedTime: number = 0;

  // 玩家速度相关
  private playerVelocityRatio: number = 0.0;
  private playerGamma: number = 1.0;
  private baseWalkSpeed: number = 8;

  // 相对论效果可视化
  private relativityEffectsGraphics!: Phaser.GameObjects.Graphics;
  private trajectoryPoints: Array<{ x: number; y: number }> = [];
  private trajectoryGraphics!: Phaser.GameObjects.Graphics;

  // 多普勒效果可视化
  private dopplerWaves: Array<{
    graphics: Phaser.GameObjects.Graphics;
    position: { x: number; y: number };
    phase: number;
  }> = [];

  constructor() {
    super({ key: 'Level15Scene' });
  }

  preload(): void {
    this.load.json('level-15', 'src/levels/levels/level-15.json');
  }

  create(): void {
    this.levelConfig = this.cache.json.get('level-15') as RelativityLevelConfig;

    if (this.levelConfig) {
      this.checkpoints = this.levelConfig.checkpoints ?? [];
      this.stages = this.levelConfig.stages ?? [];
    }

    // 初始化物理世界
    const stagePhysics = this.stages.find(s => s.stage === 1)?.physics;
    this.physicsWorld = new PhysicsWorld({
      gravity: Vec2(
        stagePhysics?.gravity?.x ?? this.levelConfig?.physics.gravity.x ?? 0,
        stagePhysics?.gravity?.y ?? this.levelConfig?.physics.gravity.y ?? -5
      ),
      allowSleep: this.levelConfig?.physics.allowSleep ?? true,
    });

    // 初始化相对论计算器
    this.relativity = new Relativity(SPEED_OF_LIGHT);

    // 效果图形层
    this.relativityEffectsGraphics = this.add.graphics();
    this.relativityEffectsGraphics.setDepth(5);

    this.trajectoryGraphics = this.add.graphics();
    this.trajectoryGraphics.setDepth(4);

    // 创建场景元素
    this.createBackground();
    this.createBoundaries();
    this.createPlatforms();
    this.createVelocityGates();
    this.createTimeDilationZones();
    this.createLengthContractionZones();
    this.createDopplerZones();
    this.createCheckpoints();
    this.createGoalZone();
    this.createStickman();
    this.createUI();
    this.setupInput();

    // 初始化状态
    this.gameState = 'running';
    this.currentStage = 1;
    this.completedStages = new Set();
    this.isVictory = false;
    this.elapsedTime = 0;
    this.dilatedTime = 0;
    this.playerVelocityRatio = 0.0;
    this.playerGamma = 1.0;
    this.trajectoryPoints = [];

    // 启动视觉效果更新
    this.startVisualEffects();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running' || this.isVictory) return;

    const dt = delta / 1000;
    this.elapsedTime += dt;

    // 物理步进
    this.physicsWorld.step(dt);

    // 更新相对论参数
    this.updateRelativityParameters();

    // 更新火柴人渲染（带相对论视觉效果）
    this.stickman.update();

    // 更新轨迹
    this.updateTrajectory();

    // 更新相对论效果可视化
    this.updateRelativityEffects();

    // 检查检查点碰撞
    this.checkCheckpointCollisions();

    // 检查胜利条件
    this.checkVictory();

    // 更新阶段显示
    this.updateStageDisplay();
  }

  /**
   * 更新相对论参数
   */
  private updateRelativityParameters(): void {
    const torso = this.stickman.getBody().getBody('torso');
    if (!torso) return;

    const velocity = torso.getLinearVelocity();
    const velocityMagnitude = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);

    // 计算速度比率（相对于光速）
    // 将游戏速度映射到相对论速度范围（假设最大游戏速度为 100，对应 0.99c）
    const normalizedVelocity = Math.min(velocityMagnitude / 100, 0.99);
    this.playerVelocityRatio = normalizedVelocity;

    // 计算洛伦兹因子
    try {
      this.playerGamma = this.relativity.calculateGamma(normalizedVelocity);
    } catch {
      this.playerGamma = Infinity;
    }

    // 计算时间膨胀效果
    const dt = 1 / 60; // 固定时间步长
    const timeDilationResult = this.relativity.timeDilation(dt, this.playerVelocityRatio);
    this.dilatedTime += timeDilationResult.value;

    // 更新速度显示
    this.velocityText.setText(`速度: ${this.playerVelocityRatio.toFixed(3)}c`);
    
    // 更新伽玛因子显示
    if (this.playerGamma > 100) {
      this.gammaText.setText(`γ: ∞ (接近光速!)`);
    } else {
      this.gammaText.setText(`γ: ${this.playerGamma.toFixed(2)}`);
    }

    // 更新时间显示
    this.timeDisplayText.setText(
      `实际时间: ${this.elapsedTime.toFixed(1)}s | 固有时间: ${this.dilatedTime.toFixed(1)}s`
    );
  }

  /**
   * 启动视觉效果更新循环
   */
  private startVisualEffects(): void {
    // 多普勒波动画
    this.time.addEvent({
      delay: 50,
      callback: () => {
        this.updateDopplerWaves();
      },
      loop: true,
    });

    // 光速脉冲效果
    this.time.addEvent({
      delay: 100,
      callback: () => {
        this.updateLightSpeedPulse();
      },
      loop: true,
    });
  }

  /**
   * 更新多普勒波效果
   */
  private updateDopplerWaves(): void {
    const dopplerZones = this.levelConfig?.dopplerZones ?? [];

    for (const zone of dopplerZones) {
      const waveGraphics = this.add.graphics();
      waveGraphics.setDepth(3);

      const screenPos = this.toScreenCoords(zone.position);
      const color = zone.isApproaching ? 0x4ecdc4 : 0xff6b6b;

      // 计算多普勒因子
      try {
        const dopplerResult = this.relativity.dopplerEffect(zone.velocity, zone.isApproaching);
        const waveRadius = 30 + dopplerResult.factor * 20;
        const phase = this.elapsedTime * 2;

        // 波纹扩散效果
        for (let i = 0; i < 3; i++) {
          const radius = waveRadius * (1 - (phase + i * 0.3) % 1);
          const alpha = 0.5 * (1 - (phase + i * 0.3) % 1);

          waveGraphics.lineStyle(2, color, alpha);
          waveGraphics.strokeCircle(screenPos.x, screenPos.y, radius);
        }
      } catch {
        // 忽略无效速度的计算错误
      }

      this.dopplerWaves.push({
        graphics: waveGraphics,
        position: zone.position,
        phase: this.elapsedTime,
      });

      // 清理旧波纹
      if (this.dopplerWaves.length > 50) {
        const oldWave = this.dopplerWaves.shift();
        if (oldWave) {
          oldWave.graphics.destroy();
        }
      }
    }
  }

  /**
   * 更新光速脉冲效果
   */
  private updateLightSpeedPulse(): void {
    this.relativityEffectsGraphics.clear();

    // 当速度接近光速时显示脉冲效果
    if (this.playerVelocityRatio > 0.7) {
      const headPos = this.stickman.getHeadPosition();
      const pulseIntensity = (this.playerVelocityRatio - 0.7) / 0.3;
      const pulsePhase = Math.sin(this.elapsedTime * 8) * 0.5 + 0.5;

      // 光速接近光环
      this.relativityEffectsGraphics.lineStyle(
        3 + pulseIntensity * 5,
        0xffffff,
        pulseIntensity * pulsePhase
      );
      this.relativityEffectsGraphics.strokeCircle(
        headPos.x,
        headPos.y,
        30 + pulseIntensity * 20 * pulsePhase
      );

      // 内部发光
      this.relativityEffectsGraphics.fillStyle(0xffffcc, pulseIntensity * 0.3 * pulsePhase);
      this.relativityEffectsGraphics.fillCircle(headPos.x, headPos.y, 15);
    }
  }

  /**
   * 更新轨迹显示
   */
  private updateTrajectory(): void {
    const headPos = this.stickman.getHeadPosition();

    // 添加新点（带时间膨胀标记）
    this.trajectoryPoints.push({ x: headPos.x, y: headPos.y });

    // 限制轨迹长度
    if (this.trajectoryPoints.length > 150) {
      this.trajectoryPoints.shift();
    }

    // 绘制轨迹（颜色根据速度变化）
    this.trajectoryGraphics.clear();

    if (this.trajectoryPoints.length > 1) {
      // 根据速度选择颜色
      let trajectoryColor: number;
      if (this.playerVelocityRatio < 0.3) {
        trajectoryColor = 0x28a745; // 低速绿色
      } else if (this.playerVelocityRatio < 0.7) {
        trajectoryColor = 0xedc240; // 中速金色
      } else {
        trajectoryColor = 0xff6b6b; // 高速红色
      }

      this.trajectoryGraphics.lineStyle(2, trajectoryColor, 0.5);

      const firstPoint = this.trajectoryPoints[0];
      if (firstPoint) {
        this.trajectoryGraphics.beginPath();
        this.trajectoryGraphics.moveTo(firstPoint.x, firstPoint.y);
      }

      for (let i = 1; i < this.trajectoryPoints.length; i++) {
        const point = this.trajectoryPoints[i];
        if (point) {
          this.trajectoryGraphics.lineTo(point.x, point.y);
        }
      }

      this.trajectoryGraphics.strokePath();
    }
  }

  /**
   * 更新相对论效果可视化
   */
  private updateRelativityEffects(): void {
    // 根据当前阶段显示不同效果
    switch (this.currentStage) {
      case 1:
        this.updateSpeedLimitEffect();
        break;
      case 2:
        this.updateTimeDilationEffect();
        break;
      case 3:
        this.updateLengthContractionEffect();
        break;
      case 4:
        this.updateDopplerEffect();
        break;
    }
  }

  /**
   * 光速限制效果可视化
   */
  private updateSpeedLimitEffect(): void {
    // 速度门可视化
    const velocityGates = this.levelConfig?.velocityGates ?? [];
    const gateGraphics = this.add.graphics();
    gateGraphics.setDepth(2);

    for (const gate of velocityGates) {
      const screenX = gate.position.x - gate.size.width / 2;
      const screenY = GAME_HEIGHT - gate.position.y - gate.size.height / 2;

      // 门颜色根据目标速度变化
      const colorIntensity = gate.targetVelocity;
      const gateColor = Phaser.Display.Color.GetColor(
        255 * colorIntensity,
        200 * (1 - colorIntensity),
        50
      );

      gateGraphics.fillStyle(gateColor, 0.3);
      gateGraphics.fillRect(screenX, screenY, gate.size.width, gate.size.height);
      gateGraphics.lineStyle(2, gateColor);
      gateGraphics.strokeRect(screenX, screenY, gate.size.width, gate.size.height);

      // 速度标签
      this.add.text(
        screenX + gate.size.width / 2,
        screenY - 10,
        `${gate.targetVelocity.toFixed(2)}c`,
        {
          fontSize: '10px',
          color: '#ffffff',
          fontFamily: 'Arial',
        }
      ).setOrigin(0.5);
    }
  }

  /**
   * 时间膨胀效果可视化
   */
  private updateTimeDilationEffect(): void {
    const timeDilationZones = this.levelConfig?.timeDilationZones ?? [];

    for (const zone of timeDilationZones) {
      try {
        const result = this.relativity.timeDilation(1.0, zone.velocity);
        const screenPos = this.toScreenCoords(zone.position);

        // 时间膨胀可视化（时钟动画）
        const clockRadius = 40;
        const timeAngle = (this.elapsedTime * result.value * 30) % 360;

        const clockGraphics = this.add.graphics();
        clockGraphics.setDepth(3);
        clockGraphics.lineStyle(2, 0x28a745, 0.8);
        clockGraphics.strokeCircle(screenPos.x, screenPos.y, clockRadius);

        // 时间指针
        const pointerX = screenPos.x + Math.cos(timeAngle * Math.PI / 180 - Math.PI / 2) * clockRadius * 0.8;
        const pointerY = screenPos.y + Math.sin(timeAngle * Math.PI / 180 - Math.PI / 2) * clockRadius * 0.8;
        clockGraphics.lineStyle(3, 0xffffff);
        clockGraphics.beginPath();
        clockGraphics.moveTo(screenPos.x, screenPos.y);
        clockGraphics.lineTo(pointerX, pointerY);
        clockGraphics.strokePath();

        // γ因子显示
        this.add.text(
          screenPos.x,
          screenPos.y + clockRadius + 15,
          `γ = ${result.gamma.toFixed(2)}`,
          {
            fontSize: '12px',
            color: '#28a745',
            fontFamily: 'Arial',
          }
        ).setOrigin(0.5);
      } catch {
        // 忽略计算错误
      }
    }
  }

  /**
   * 长度收缩效果可视化
   */
  private updateLengthContractionEffect(): void {
    const lengthContractionZones = this.levelConfig?.lengthContractionZones ?? [];

    for (const zone of lengthContractionZones) {
      try {
        const result = this.relativity.lengthContraction(100.0, zone.velocity);
        const screenPos = this.toScreenCoords(zone.position);

        // 原始长度（白色）
        const originalGraphics = this.add.graphics();
        originalGraphics.setDepth(3);
        originalGraphics.fillStyle(0xffffff, 0.2);
        originalGraphics.fillRect(
          screenPos.x - 50,
          screenPos.y - 10,
          100,
          20
        );

        // 收缩后长度（蓝色）
        const contractedGraphics = this.add.graphics();
        contractedGraphics.setDepth(4);
        contractedGraphics.fillStyle(0x00ccff, 0.6);
        contractedGraphics.fillRect(
          screenPos.x - result.value / 2,
          screenPos.y + 20,
          result.value,
          20
        );

        // 显示收缩比例
        this.add.text(
          screenPos.x,
          screenPos.y + 50,
          `L' = L/γ = ${result.value.toFixed(1)}`,
          {
            fontSize: '12px',
            color: '#00ccff',
            fontFamily: 'Arial',
          }
        ).setOrigin(0.5);
      } catch {
        // 忽略计算错误
      }
    }
  }

  /**
   * 多普勒效果可视化
   */
  private updateDopplerEffect(): void {
    // 在 dopplerWaves 更新中已处理
  }

  private createBackground(): void {
    this.cameras.main.setBackgroundColor('#0a0a1e');

    // 相对论主题网格
    const grid = this.add.graphics();
    grid.lineStyle(1, 0x2a2a4a, 0.2);
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      grid.beginPath();
      grid.moveTo(x, 0);
      grid.lineTo(x, GAME_HEIGHT);
      grid.strokePath();
    }
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      grid.beginPath();
      grid.moveTo(0, y);
      grid.lineTo(GAME_WIDTH, y);
      grid.strokePath();
    }

    // 光速标记线
    const lightSpeedLine = this.add.graphics();
    lightSpeedLine.lineStyle(2, 0xedc240, 0.5);
    lightSpeedLine.beginPath();
    lightSpeedLine.moveTo(0, GAME_HEIGHT / 2);
    lightSpeedLine.lineTo(GAME_WIDTH, GAME_HEIGHT / 2);
    lightSpeedLine.strokePath();

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 15, '--- 光速参考线 c = 1 ---', {
      fontSize: '10px',
      color: '#edc240',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  private createBoundaries(): void {
    this.physicsWorld.createBody('boundary', {
      x: GAME_WIDTH / 2,
      y: 0,
      type: 'static',
      isBoundary: true,
      boundarySize: { width: GAME_WIDTH, height: GAME_HEIGHT },
    });

    const boundaryGraphics = this.add.graphics();
    boundaryGraphics.lineStyle(2, 0x333333);
    boundaryGraphics.strokeRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  private createPlatforms(): void {
    const platforms = this.levelConfig?.platforms ?? [];
    const platformGraphics = this.add.graphics();

    for (const platform of platforms) {
      this.physicsWorld.createBody(platform.id, {
        x: platform.position.x,
        y: platform.position.y,
        type: 'static',
        friction: platform.friction ?? 0.5,
        restitution: platform.restitution ?? 0,
      });

      const screenX = platform.position.x - platform.size.width / 2;
      const screenY = GAME_HEIGHT - platform.position.y - platform.size.height / 2;
      const color = platform.color ?? 0x4a4a6a;

      platformGraphics.fillStyle(color, 1);
      platformGraphics.fillRect(screenX, screenY, platform.size.width, platform.size.height);
    }
  }

  private createVelocityGates(): void {
    const velocityGates = this.levelConfig?.velocityGates ?? [];
    const gateGraphics = this.add.graphics();
    gateGraphics.setDepth(2);

    for (const gate of velocityGates) {
      const screenX = gate.position.x - gate.size.width / 2;
      const screenY = GAME_HEIGHT - gate.position.y - gate.size.height / 2;

      // 根据目标速度确定颜色
      const intensity = gate.targetVelocity;
      const color = Phaser.Display.Color.GetColor(
        255 * intensity,
        200 * (1 - intensity),
        50
      );

      gateGraphics.fillStyle(color, 0.3);
      gateGraphics.fillRect(screenX, screenY, gate.size.width, gate.size.height);
      gateGraphics.lineStyle(2, color);
      gateGraphics.strokeRect(screenX, screenY, gate.size.width, gate.size.height);

      this.add.text(screenX + gate.size.width / 2, screenY - 10, `目标: ${gate.targetVelocity.toFixed(2)}c`, {
        fontSize: '12px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createTimeDilationZones(): void {
    const timeDilationZones = this.levelConfig?.timeDilationZones ?? [];
    const zoneGraphics = this.add.graphics();
    zoneGraphics.setDepth(2);

    for (const zone of timeDilationZones) {
      const screenX = zone.position.x - zone.size.width / 2;
      const screenY = GAME_HEIGHT - zone.position.y - zone.size.height / 2;

      zoneGraphics.fillStyle(0x28a745, 0.15);
      zoneGraphics.fillRect(screenX, screenY, zone.size.width, zone.size.height);
      zoneGraphics.lineStyle(3, 0x28a745, 0.6);
      zoneGraphics.strokeRect(screenX, screenY, zone.size.width, zone.size.height);

      this.add.text(screenX + zone.size.width / 2, screenY - 15, `时间膨胀区 v=${zone.velocity.toFixed(2)}c`, {
        fontSize: '12px',
        color: '#28a745',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createLengthContractionZones(): void {
    const lengthContractionZones = this.levelConfig?.lengthContractionZones ?? [];
    const zoneGraphics = this.add.graphics();
    zoneGraphics.setDepth(2);

    for (const zone of lengthContractionZones) {
      const screenX = zone.position.x - zone.size.width / 2;
      const screenY = GAME_HEIGHT - zone.position.y - zone.size.height / 2;

      zoneGraphics.fillStyle(0x00ccff, 0.15);
      zoneGraphics.fillRect(screenX, screenY, zone.size.width, zone.size.height);
      zoneGraphics.lineStyle(3, 0x00ccff, 0.6);
      zoneGraphics.strokeRect(screenX, screenY, zone.size.width, zone.size.height);

      this.add.text(screenX + zone.size.width / 2, screenY - 15, `长度收缩区 v=${zone.velocity.toFixed(2)}c`, {
        fontSize: '12px',
        color: '#00ccff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createDopplerZones(): void {
    const dopplerZones = this.levelConfig?.dopplerZones ?? [];
    const zoneGraphics = this.add.graphics();
    zoneGraphics.setDepth(2);

    for (const zone of dopplerZones) {
      const screenX = zone.position.x - zone.size.width / 2;
      const screenY = GAME_HEIGHT - zone.position.y - zone.size.height / 2;

      const color = zone.isApproaching ? 0x4ecdc4 : 0xff6b6b;
      const label = zone.isApproaching ? '蓝移区（接近）' : '红移区（远离）';

      zoneGraphics.fillStyle(color, 0.15);
      zoneGraphics.fillRect(screenX, screenY, zone.size.width, zone.size.height);
      zoneGraphics.lineStyle(3, color, 0.6);
      zoneGraphics.strokeRect(screenX, screenY, zone.size.width, zone.size.height);

      this.add.text(screenX + zone.size.width / 2, screenY - 15, label, {
        fontSize: '12px',
        color: zone.isApproaching ? '#4ecdc4' : '#ff6b6b',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createCheckpoints(): void {
    for (const checkpoint of this.checkpoints) {
      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const color = checkpoint.color ?? CHECKPOINT_COLOR;

      const graphics = this.add.graphics();
      graphics.fillStyle(color, 0.3);
      graphics.fillRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);
      graphics.lineStyle(3, color);
      graphics.strokeRect(screenX, screenY, checkpoint.size.width, checkpoint.size.height);

      this.checkpointGraphics.set(checkpoint.id, graphics);

      const stageLabel = this.stages.find(s => s.stage === checkpoint.stage);
      this.add.text(screenX + checkpoint.size.width / 2, screenY - 15, `阶段${checkpoint.stage}: ${stageLabel?.name ?? ''}`, {
        fontSize: '12px',
        color: '#ffa500',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private createGoalZone(): void {
    const goal = this.levelConfig?.goal;
    if (!goal) return;

    const screenX = goal.position.x - goal.size.width / 2;
    const screenY = GAME_HEIGHT - goal.position.y - goal.size.height / 2;

    this.goalZone = new Phaser.Geom.Rectangle(
      screenX,
      screenY,
      goal.size.width,
      goal.size.height
    );

    const goalGraphics = this.add.graphics();
    goalGraphics.fillStyle(GOAL_COLOR, 0.5);
    goalGraphics.fillRectShape(this.goalZone);
    goalGraphics.lineStyle(3, GOAL_COLOR);
    goalGraphics.strokeRectShape(this.goalZone);

    // 终点脉冲动画
    this.time.addEvent({
      delay: 500,
      callback: () => {
        goalGraphics.clear();
        const pulseScale = 1 + Math.sin(this.elapsedTime * 2) * 0.1;
        goalGraphics.fillStyle(GOAL_COLOR, 0.5 * pulseScale);
        goalGraphics.fillRectShape(this.goalZone);
        goalGraphics.lineStyle(3, GOAL_COLOR);
        goalGraphics.strokeRectShape(this.goalZone);
      },
      loop: true,
    });

    this.add.text(this.goalZone.centerX, this.goalZone.y - 15, '终点', {
      fontSize: '14px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  private createStickman(): void {
    const playerConfig = this.levelConfig?.player;
    const startY = playerConfig?.startPosition.y ?? 300;
    const startX = playerConfig?.startPosition.x ?? 100;

    // 根据当前阶段调整颜色
    const stageColor = STAGE_COLORS[this.currentStage];

    this.stickman = new Stickman(
      this,
      this.physicsWorld,
      {
        x: startX,
        y: GAME_HEIGHT - startY,
        scale: 1,
        walkSpeed: this.baseWalkSpeed,
        jumpForce: 150,
        colors: {
          line: stageColor,
          headFill: 0x1a1a2e,
          lineWidth: 3,
        },
      },
      GAME_HEIGHT
    );
  }

  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '相对论综合挑战 - 关卡15', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 阶段标题
    this.stageText = this.add.text(GAME_WIDTH / 2, 60, '', {
      fontSize: '18px',
      color: '#edc240',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(GAME_WIDTH / 2, 85, '', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 进度文本
    this.progressText = this.add.text(10, 10, '进度: 阶段1/4', {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 速度显示
    this.velocityText = this.add.text(10, 30, '速度: 0.000c', {
      fontSize: '14px',
      color: '#edc240',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 伽玛因子显示
    this.gammaText = this.add.text(10, 50, 'γ: 1.00', {
      fontSize: '14px',
      color: '#28a745',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 时间显示
    this.timeDisplayText = this.add.text(10, 70, '实际时间: 0.0s | 固有时间: 0.0s', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 5, y: 3 },
    });

    // 公式展示
    this.formulaText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 30, '', {
      fontSize: '16px',
      color: '#00ff00',
      fontFamily: 'Arial',
      backgroundColor: '#1a1a2e',
      padding: { x: 10, y: 5 },
    }).setOrigin(0.5);

    this.createStageIndicators();
    this.updateStageDisplay();
  }

  private createStageIndicators(): void {
    const startX = GAME_WIDTH - 320;
    const y = 10;
    const width = 75;
    const gap = 5;

    for (let i = 1; i <= 4; i++) {
      const x = startX + (i - 1) * (width + gap);
      const stageConfig = this.stages.find(s => s.stage === i);

      const bg = this.add.graphics();
      bg.fillStyle(STAGE_COLORS[i as CurrentStage], 0.3);
      bg.fillRect(x, y, width, 35);
      bg.lineStyle(2, STAGE_COLORS[i as CurrentStage]);
      bg.strokeRect(x, y, width, 35);

      this.add.text(x + width / 2, y + 17, stageConfig?.name ?? `阶段${i}`, {
        fontSize: '10px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
  }

  private setupInput(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'r' || event.key === 'R') {
        this.resetLevel();
      }
      // 模拟速度调整（按 V 键）
      if (event.key === 'v' || event.key === 'V') {
        this.simulateVelocityChange();
      }
    });
  }

  /**
   * 模拟速度变化（用于测试相对论效果）
   */
  private simulateVelocityChange(): void {
    const torso = this.stickman.getBody().getBody('torso');
    if (!torso) return;

    // 增加速度以测试相对论效应
    const currentVel = torso.getLinearVelocity();
    const speedBoost = 15;
    torso.setLinearVelocity(Vec2(currentVel.x + speedBoost, currentVel.y));
  }

  private checkCheckpointCollisions(): void {
    const headPos = this.stickman.getHeadPosition();

    for (const checkpoint of this.checkpoints) {
      if (this.completedStages.has(checkpoint.stage ?? 0)) continue;

      const screenX = checkpoint.position.x - checkpoint.size.width / 2;
      const screenY = GAME_HEIGHT - checkpoint.position.y - checkpoint.size.height / 2;
      const checkpointRect = new Phaser.Geom.Rectangle(
        screenX,
        screenY,
        checkpoint.size.width,
        checkpoint.size.height
      );

      if (checkpointRect.contains(headPos.x, headPos.y)) {
        this.completeStage(checkpoint.stage ?? 1);
      }
    }
  }

  private completeStage(stage: number): void {
    if (this.completedStages.has(stage)) return;

    this.completedStages.add(stage);

    const graphics = this.checkpointGraphics.get(`checkpoint-${stage}`);
    if (graphics) {
      graphics.clear();
      graphics.fillStyle(0x4ade80, 0.5);
      graphics.fillRect(
        graphics.x ?? 0,
        graphics.y ?? 0,
        this.checkpoints.find(c => c.stage === stage)?.size.width ?? 60,
        this.checkpoints.find(c => c.stage === stage)?.size.height ?? 80
      );
    }

    this.showStageTransition(stage);

    if (stage < 4) {
      this.currentStage = (stage + 1) as CurrentStage;
      this.updatePhysicsForStage(this.currentStage);
      this.updateStickmanColor();
    }
  }

  /**
   * 根据阶段更新物理参数
   */
  private updatePhysicsForStage(_stage: CurrentStage): void {
    // 注：PhysicsWorld 不支持动态修改重力
    // 各阶段的物理差异通过其他方式实现
  }

  /**
   * 更新火柴人颜色（根据阶段）
   */
  private updateStickmanColor(): void {
    const stageColor = STAGE_COLORS[this.currentStage];
    this.stickman.setColors({
      line: stageColor,
      headFill: 0x1a1a2e,
      lineWidth: 3,
    });
  }

  private showStageTransition(stage: number): void {
    const stageConfig = this.stages.find(s => s.stage === stage);
    const transitionBg = this.add.graphics();
    transitionBg.fillStyle(0x000000, 0.7);
    transitionBg.fillRect(GAME_WIDTH / 2 - 150, GAME_HEIGHT / 2 - 60, 300, 120);

    const stageColor = STAGE_COLORS[stage as CurrentStage];
    const colorHex = `#${stageColor.toString(16).padStart(6, '0')}`;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, `阶段${stage} 完成！`, {
      fontSize: '24px',
      color: colorHex,
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    if (stageConfig) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, stageConfig.name, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }

    this.time.delayedCall(1500, () => {
      transitionBg.destroy();
    });
  }

  private updateStageDisplay(): void {
    const stageConfig = this.stages.find(s => s.stage === this.currentStage);
    if (stageConfig) {
      this.stageText.setText(`当前: ${stageConfig.name}`);
      const stageColor = STAGE_COLORS[this.currentStage];
      this.stageText.setColor(`#${stageColor.toString(16).padStart(6, '0')}`);
      this.hintText.setText(stageConfig.hint ?? '');
    }
    this.progressText.setText(`进度: 阶段${this.currentStage}/4 (${this.completedStages.size}/4 完成)`);
    this.updateFormulaDisplay();
  }

  /**
   * 更新公式显示（根据当前阶段）
   */
  private updateFormulaDisplay(): void {
    let formula = '';
    switch (this.currentStage) {
      case 1:
        formula = '速度叠加: v = (v₁+v₂)/(1+v₁v₂/c²)';
        break;
      case 2:
        formula = '时间膨胀: t\' = γt = t/√(1-v²/c²)';
        break;
      case 3:
        formula = '长度收缩: L\' = L/γ';
        break;
      case 4:
        formula = '多普勒效应: D = √((1±β)/(1∓β))';
        break;
    }
    this.formulaText.setText(formula);
  }

  private checkVictory(): void {
    const headPos = this.stickman.getHeadPosition();

    if (this.goalZone.contains(headPos.x, headPos.y) && this.completedStages.size >= 3) {
      this.isVictory = true;
      this.gameState = 'success';
      this.showVictoryMessage();
    }
  }

  private showVictoryMessage(): void {
    const saveGameState = getGameState();
    saveGameState.completeLevel(15, this.elapsedTime);

    const victoryBg = this.add.graphics();
    victoryBg.fillStyle(0x000000, 0.8);
    victoryBg.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 100, '🎉 相对论综合挑战完成！', {
      fontSize: '36px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 50,
      '你已掌握了狭义相对论的核心概念！',
      {
        fontSize: '18px',
        color: '#ffffff',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 20,
      '光速限制 | 时间膨胀 | 长度收缩 | 红移蓝移',
      {
        fontSize: '16px',
        color: '#edc240',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 + 10,
      `γ = 1/√(1-v²/c²) — 洛伦兹因子是所有相对论效应的核心`,
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
        align: 'center',
      }
    ).setOrigin(0.5);

    const backBtn = this.add.text(GAME_WIDTH / 2 - 80, GAME_HEIGHT / 2 + 60, '返回', {
      fontSize: '20px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2 + 80, GAME_HEIGHT / 2 + 60, '重新挑战', {
      fontSize: '20px',
      color: '#ffd700',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 20, y: 10 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetLevel();
    });
  }

  private resetLevel(): void {
    this.scene.restart();
  }

  /**
   * 物理坐标转屏幕坐标
   */
  private toScreenCoords(pos: { x: number; y: number }): { x: number; y: number } {
    return {
      x: pos.x,
      y: GAME_HEIGHT - pos.y,
    };
  }

  // 公开访问方法
  getCurrentStage(): CurrentStage {
    return this.currentStage;
  }

  getCompletedStages(): Set<number> {
    return this.completedStages;
  }

  getIsVictory(): boolean {
    return this.isVictory;
  }

  getPhysicsWorld(): PhysicsWorld {
    return this.physicsWorld;
  }

  getStickman(): Stickman {
    return this.stickman;
  }

  getGameState(): GameState {
    return this.gameState;
  }

  getRelativity(): Relativity {
    return this.relativity;
  }

  getPlayerVelocityRatio(): number {
    return this.playerVelocityRatio;
  }

  getPlayerGamma(): number {
    return this.playerGamma;
  }
}